import React, { useState } from 'react';
import { countryName } from '../../lib/countries';
import { CaretLeft, CaretRight, X, ArrowRight, MapPin } from '@phosphor-icons/react';

const typeLabel = (asset) => {
  if (!asset) return null;
  const t = (asset.asset_type || '').replace(/_/g, ' ');
  const beds = asset.bedrooms;
  if (beds && t) return `${beds}-bedroom ${t}`;
  return t || null;
};

const assetLocation = (asset, project) => {
  const parts = [asset?.city || project?.city, countryName(asset?.country_code || project?.country_code)];
  const clean = parts.filter(Boolean);
  if (clean.length) return clean.join(', ');
  return project?.location && project.location.trim() !== ',' ? project.location : null;
};

export const PropertyLightbox = ({ images, startIndex = 0, onClose, title }) => {
  const [index, setIndex] = useState(startIndex);
  if (!images?.length) return null;
  return (
    <div
      className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 md:p-10"
      onClick={onClose}
      data-testid="property-lightbox"
    >
      <div
        className="w-full max-w-4xl max-h-[86vh] flex flex-col bg-white rounded-2xl overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
      <div className="flex items-center justify-between px-5 py-3.5 border-b border-black/[0.06]">
        <span className="text-sm text-foreground-muted">{title} · {index + 1} / {images.length}</span>
        <button onClick={onClose} className="p-2 rounded-full hover:bg-black/5" data-testid="lightbox-close">
          <X size={20} />
        </button>
      </div>
      <div className="relative flex items-center justify-center bg-black/[0.04] p-3 md:p-4">
        <img
          src={images[index]}
          alt={`${title} ${index + 1}`}
          className="max-h-[58vh] w-auto max-w-full object-contain rounded-xl"
        />
        {images.length > 1 && (
          <>
            <button
              onClick={() => setIndex((p) => (p === 0 ? images.length - 1 : p - 1))}
              className="absolute left-5 p-2.5 rounded-full bg-white/90 hover:bg-white text-foreground shadow-md transition-colors"
              data-testid="lightbox-prev"
            >
              <CaretLeft size={22} weight="bold" />
            </button>
            <button
              onClick={() => setIndex((p) => (p === images.length - 1 ? 0 : p + 1))}
              className="absolute right-5 p-2.5 rounded-full bg-white/90 hover:bg-white text-foreground shadow-md transition-colors"
              data-testid="lightbox-next"
            >
              <CaretRight size={22} weight="bold" />
            </button>
          </>
        )}
      </div>
      {images.length > 1 && (
        <div className="flex gap-2 overflow-x-auto px-4 py-3 scrollbar-hide border-t border-black/[0.06]">
          {images.map((url, i) => (
            <button
              key={i}
              onClick={() => setIndex(i)}
              className={`h-14 w-20 flex-shrink-0 rounded-lg overflow-hidden border-2 transition-[border-color,opacity] duration-200 ${i === index ? 'border-primary' : 'border-transparent opacity-55 hover:opacity-100'}`}
            >
              <img src={url} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
      </div>
    </div>
  );
};

export const PropertyHero = ({ asset, project, images, onOpenDetails }) => {
  const [mobileIndex, setMobileIndex] = useState(0);
  const [lightbox, setLightbox] = useState(null);
  const pics = images || [];
  const name = asset?.name || project?.title;
  const sub = typeLabel(asset);
  const loc = assetLocation(asset, project);

  return (
    <section data-testid="property-hero">
      {/* Mobile / tablet: large swipeable image */}
      <div className="lg:hidden">
        {pics.length > 0 && (
          <div className="relative -mx-6 md:mx-0">
            <div
              className="flex overflow-x-auto snap-x snap-mandatory scrollbar-hide"
              onScroll={(e) => {
                const w = e.currentTarget.clientWidth || 1;
                setMobileIndex(Math.round(e.currentTarget.scrollLeft / w));
              }}
              data-testid="property-hero-swiper"
            >
              {pics.map((url, i) => (
                <button
                  key={i}
                  onClick={() => setLightbox(i)}
                  className="snap-center flex-shrink-0 w-full h-[260px] sm:h-[340px] md:rounded-2xl overflow-hidden bg-black/[0.05]"
                >
                  <img src={url} alt={`${name} ${i + 1}`} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
            <div className="absolute bottom-3 left-6 right-6 flex items-center justify-between">
              <span className="px-2.5 py-1 rounded-full text-[11px] font-medium bg-black/55 text-white backdrop-blur-sm" data-testid="property-hero-counter">
                {mobileIndex + 1} / {pics.length}
              </span>
              <button
                onClick={() => setLightbox(0)}
                className="px-3 py-1 rounded-full text-[11px] font-medium bg-white/90 text-foreground backdrop-blur-sm"
                data-testid="property-hero-view-gallery"
              >
                View gallery
              </button>
            </div>
          </div>
        )}
        <div className="mt-5">
          <h2 className="text-2xl font-heading font-semibold tracking-tight" data-testid="property-name">{name}</h2>
          <p className="text-sm text-foreground-muted mt-1 capitalize">
            {[sub, loc].filter(Boolean).join(' · ')}
          </p>
          {onOpenDetails && (
            <button
              onClick={onOpenDetails}
              className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-primary"
              data-testid="property-details-link-mobile"
            >
              Property details <ArrowRight size={15} weight="bold" />
            </button>
          )}
        </div>
      </div>

      {/* Desktop: gallery across the top, written detail underneath */}
      <div className="hidden lg:block">
        <div className="grid grid-cols-3 grid-rows-2 gap-3 h-[420px]">
          {pics[0] && (
            <button
              onClick={() => setLightbox(0)}
              className={`${pics.length > 1 ? 'col-span-2' : 'col-span-3'} row-span-2 rounded-2xl overflow-hidden group relative bg-black/[0.05]`}
              data-testid="property-hero-primary"
            >
              <img src={pics[0]} alt={name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]" />
            </button>
          )}
          {pics[1] && (
            <button onClick={() => setLightbox(1)} className="rounded-2xl overflow-hidden group bg-black/[0.05]">
              <img src={pics[1]} alt="" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]" />
            </button>
          )}
          {pics[2] && (
            <button onClick={() => setLightbox(2)} className="relative rounded-2xl overflow-hidden group bg-black/[0.05]" data-testid="property-hero-view-all">
              <img src={pics[2]} alt="" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]" />
              {pics.length > 3 && (
                <span className="absolute inset-0 bg-black/45 text-white text-sm font-medium flex items-center justify-center backdrop-blur-[2px]">
                  View all {pics.length}
                </span>
              )}
            </button>
          )}
        </div>

        <div className="mt-8">
          <h2 className="text-3xl font-heading font-semibold tracking-tight leading-tight" data-testid="property-name-desktop">{name}</h2>
          {sub && <p className="text-sm text-foreground-muted mt-2 capitalize">{sub}</p>}
          {loc && (
            <p className="text-sm text-foreground-muted mt-1 flex items-center gap-1.5">
              <MapPin size={15} weight="bold" className="text-primary" /> {loc}
            </p>
          )}
          {asset?.description && (
            <p className="text-sm text-foreground-muted leading-relaxed mt-5 max-w-3xl">{asset.description}</p>
          )}
          <div className="flex flex-wrap gap-x-8 gap-y-3 mt-6">
            {[
              ['Strategy', project?.project_type_label],
              ['Internal area', asset?.usable_area_sqm ? `${asset.usable_area_sqm} m²` : null],
              ['Bedrooms', asset?.bedrooms],
              ['Bathrooms', asset?.bathrooms],
            ]
              .filter(([, v]) => v !== null && v !== undefined && v !== '')
              .map(([label, value]) => (
                <div key={label}>
                  <p className="text-[11px] uppercase tracking-[0.14em] text-foreground-muted">{label}</p>
                  <p className="text-sm font-medium mt-0.5">{value}</p>
                </div>
              ))}
          </div>
          {onOpenDetails && (
            <button
              onClick={onOpenDetails}
              className="mt-7 inline-flex items-center gap-2 text-sm font-medium text-primary hover:gap-3 transition-all"
              data-testid="property-details-link"
            >
              Property details <ArrowRight size={16} weight="bold" />
            </button>
          )}
        </div>
      </div>

      {lightbox !== null && (
        <PropertyLightbox images={pics} startIndex={lightbox} title={name} onClose={() => setLightbox(null)} />
      )}
    </section>
  );
};

export default PropertyHero;
